import _defaultTo from 'lodash/defaultTo';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
// Serialization
import { deserialize, serialize } from 'serializr';
import {
  bookingSchema,
  extendBookingSchema,
  carparkBookingOptionSchema,
  bookingSearchInputSchema,
  bookingExtendSearchInputSchema,
  bookingPayOverstayInputSchema,
  bookingPayOverstaySchema,
  bookingAddSchema,
  bookingUpdateSchema,
  bookingInputSchema,
  extendBookingInputSchema,
  bookingOneparkSchema,
} from './schemas';
// Models
import mParking from './parking';
// Permissions
import permissions from '../../constants/Permissions';
// Fragments
import {
  myBookingFragment,
  myBookingForListFragment,
  bookingFragment,
  purchasesForMyBookingFragment,
  carparkBookingOptionFragment,
  operationFragment,
  oneparkBookingFragment,
  offerFragment,
  addedBookingOfferFragment,
  searchOfferFragment,
  myBookingWithPurchasesOnlyFragment,
} from './fragments';
// Color
import { parkingStatusPalette } from '../../components/core/colors';
// Custom
import { SEARCHFIELD_TYPES } from '../../constants/App';
import { findInList } from '../../utils/Tools';

// Permissions used in model
const {
  pCpm: { parkingCategoryRead, customerRead, bookingWrite },
} = permissions;

/**
 * Serialization
 */
const serializeBookingSearch = ({
  begin, carpark, discount, end, spacesQuantity,
}) => ({
  ...serialize(bookingSearchInputSchema, {
    ...(!_isNil(begin) && { begin }),
    ...(!_isNil(carpark) && { carpark }),
    ...(!_isNil(discount) && { discount }),
    ...(!_isNil(end) && { end }),
    ...(!_isNil(spacesQuantity) && { spacesQuantity }),
  }),
});

const serializeExtendBookingSearch = ({
  carparkId, end, id,
}) => ({
  ...serialize(bookingExtendSearchInputSchema, {
    ...(!_isNil(carparkId) && { carparkId }),
    ...(!_isNil(end) && { end }),
    ...(!_isNil(id) && { id }),
  }),
});

const serializePayBookingOverstay = ({
  carparkId, id,
}) => ({
  ...serialize(bookingPayOverstayInputSchema, {
    ...(!_isNil(carparkId) && { carparkId }),
    ...(!_isNil(id) && { id }),
  }),
});

const serializeBooking = ({ offerReference, vehicles }) => ({
  ...serialize(bookingInputSchema, {
    ...(!_isNil(offerReference) && { offerReference }),
    ...(!_isNil(vehicles) && { vehicles }),
  }),
});

const serializeExtendBooking = ({
  carparkId, id, offerReference, sendPaymentLinkToCustomer,
}) => ({
  ...serialize(extendBookingInputSchema, {
    ...(!_isNil(carparkId) && { carparkId }),
    ...(!_isNil(id) && { id }),
    ...(!_isNil(offerReference) && { offerReference }),
    ...(!_isNil(sendPaymentLinkToCustomer) && { sendPaymentLinkToCustomer }),
  }),
});

const serializeBookingAdd = ({
  automaticKeypadCodesQuantity,
  comments,
  customerId,
  keypadCodes,
  vehicles,
  insideQuantity,
  outsideQuantity,
  outsideQuantityPermanentExit,
  doors,
  restrictedByPermanentExit,
  restrictedByAntipassbackEntrance,
  restrictedByAntipassbackExit,
  sendPaymentLinkToCustomer,
  offerReference,
}) => ({
  ...serialize(bookingAddSchema, {
    ...(!_isNil(automaticKeypadCodesQuantity) && {
      automaticKeypadCodesQuantity,
    }),
    ...(!_isNil(comments) && { comments }),
    customerId,
    doors,
    ...(!_isNil(keypadCodes) && { keypadCodes }),
    ...(!_isNil(offerReference) && { offerReference }),
    ...(!_isNil(restrictedByPermanentExit) && { restrictedByPermanentExit }),
    ...(!_isNil(restrictedByAntipassbackEntrance) && {
      restrictedByAntipassbackEntrance,
    }),
    ...(!_isNil(restrictedByAntipassbackExit) && {
      restrictedByAntipassbackExit,
    }),
    ...(!_isNil(sendPaymentLinkToCustomer) && {
      sendPaymentLinkToCustomer,
    }),
    state: {
      insideQuantity: _defaultTo(insideQuantity, null),
      ...(!_isNil(outsideQuantity) && { outsideQuantity }),
      ...(!_isNil(outsideQuantityPermanentExit) && {
        outsideQuantityPermanentExit,
      }),
    },
    vehicles,
  }),
});

const serializeBookingUpdate = ({
  comments,
  keypadCodes,
  vehicles,
  doors,
  restrictedByForbiddenEntry,
  restrictedByPermanentExit,
  restrictedByForbiddenExit,
  restrictedByAntipassbackEntrance,
  restrictedByAntipassbackExit,
}) => ({
  ...serialize(bookingUpdateSchema, {
    ...(!_isNil(comments) && { comments }),
    keypadCodes,
    doors,
    ...(!_isNil(restrictedByForbiddenEntry) && { restrictedByForbiddenEntry }),
    ...(!_isNil(restrictedByPermanentExit) && { restrictedByPermanentExit }),
    ...(!_isNil(restrictedByForbiddenExit) && { restrictedByForbiddenExit }),
    ...(!_isNil(restrictedByAntipassbackEntrance) && {
      restrictedByAntipassbackEntrance,
    }),
    ...(!_isNil(restrictedByAntipassbackExit) && {
      restrictedByAntipassbackExit,
    }),
    vehicles,
  }),
});

const deserializeBookingOption = (data) => deserialize(carparkBookingOptionSchema, data);
const deserializeBooking = (data) => deserialize(bookingSchema, data);
const deserializeExtendBooking = (data) => deserialize(extendBookingSchema, data);
const deserializePayBookingOverstay = (data) => deserialize(bookingPayOverstaySchema, data);
const deserializeBookingOnepark = (data) => deserialize(bookingOneparkSchema, data);

const bookingOneparkStatusList = [
  {
    key: mParking.STATUS_LIST.CREATED,
    value: mParking.STATUS_LIST.CREATED,
    labelId: 'booking.status.created',
    icon: 'parkingStatusCreated',
    color: parkingStatusPalette.created,
  },
  {
    key: mParking.STATUS_LIST.FINISHED,
    value: mParking.STATUS_LIST.FINISHED,
    labelId: 'booking.status.finished',
    icon: 'parkingStatusFinished',
    color: parkingStatusPalette.finished,
  },
  {
    key: mParking.STATUS_LIST.STARTED,
    value: mParking.STATUS_LIST.STARTED,
    labelId: 'booking.status.started',
    icon: 'parkingStatusStarted',
    color: parkingStatusPalette.started,
  },
];

const bookingOneparkUsageList = [
  {
    key: 'b2b_subscription',
    value: 'b2b_subscription',
    labelId: 'booking.onepark.b2b_subscription',
  },
  {
    key: 'package',
    value: 'package',
    labelId: 'booking.onepark.package',
  },
  {
    key: 'subscription',
    value: 'subscription',
    labelId: 'booking.onepark.subscription',
  },
];

const bookingStatusList = [
  {
    key: mParking.STATUS_LIST.CREATED,
    value: mParking.STATUS_LIST.CREATED,
    labelId: 'booking.status.created',
    icon: 'parkingStatusCreated',
    color: parkingStatusPalette.created,
  },
  {
    key: mParking.STATUS_LIST.IN_PROGRESS,
    value: mParking.STATUS_LIST.IN_PROGRESS,
    labelId: 'booking.status.inProgress',
    icon: 'parkingStatusInProgress',
    color: parkingStatusPalette.inProgress,
  },
  {
    key: mParking.STATUS_LIST.STARTED,
    value: mParking.STATUS_LIST.STARTED,
    labelId: 'booking.status.started',
    icon: 'parkingStatusStarted',
    color: parkingStatusPalette.started,
  },
  {
    key: mParking.STATUS_LIST.IN_OVERSTAY,
    value: mParking.STATUS_LIST.IN_OVERSTAY,
    labelId: 'booking.status.inOverstay',
    icon: 'parkingStatusInOverstay',
    color: parkingStatusPalette.inOverstay,
  },
  {
    key: mParking.STATUS_LIST.FINISHED,
    value: mParking.STATUS_LIST.FINISHED,
    labelId: 'booking.status.finished',
    icon: 'parkingStatusFinished',
    color: parkingStatusPalette.finished,
  },
  {
    key: mParking.STATUS_LIST.CANCELLED,
    value: mParking.STATUS_LIST.CANCELLED,
    labelId: 'booking.status.cancelled',
    icon: 'parkingStatusCancelled',
    color: parkingStatusPalette.cancelled,
  },
];

/**
 * Utils
 */

const bookingStatus = (booking) => bookingStatusList.find(({ value }) => value === booking.status);
const isBookingCreated = (booking) => bookingStatus(booking).value === mParking.STATUS_LIST.CREATED;
const isBookingInProgress = (booking) => bookingStatus(booking).value === mParking.STATUS_LIST.IN_PROGRESS;
const isBookingStarted = (booking) => bookingStatus(booking).value === mParking.STATUS_LIST.STARTED;
const isBookingCancelled = (booking) => bookingStatus(booking).value === mParking.STATUS_LIST.CANCELLED;
const isBookingFinished = (booking) => bookingStatus(booking).value === mParking.STATUS_LIST.FINISHED;
const isBookingInOverstay = (booking) => bookingStatus(booking).value === mParking.STATUS_LIST.IN_OVERSTAY;
const bookingOneparkStatus = (booking) => findInList(bookingOneparkStatusList, 'value', booking, 'status');

/**
 * GraphQL
 */
const queryPermissions = (hasPermission) => ({
  withCustomer: hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }),
  withParkingCategory: hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }),
});

const queListBookings = (hasPermission) => `
  query ListBookings($input: ListBookingsInput!, $pagination: PaginationInput, $search: ListBookingsSearchInput, $sort: [ListBookingsSortInput]) {
    listBookings(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...bookingFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${bookingFragment(queryPermissions(hasPermission))}
`;

const queListMyBookings = `
  query ListMyBookings($pagination: PaginationInput, $search: ListMyBookingsSearchInput, $sort: [ListMyBookingsSortInput!]) {
    listMyBookings(pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...myBookingForListFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${myBookingForListFragment}
`;

const queListMyBookingOptions = `
  query ListMyBookingOptions($input: ListMyBookingOptionsInput!) {
    listMyBookingOptions(input: $input) {
      ...carparkBookingOptionFragment
    }
  }

  ${carparkBookingOptionFragment}
`;

const queSearchBookingOffer = `
  query Search($input: SearchInput!) {
    search(input: $input) {
        ...searchOfferFragment
    }
  }

  ${searchOfferFragment}
`;

const mutSearchBookingExtensionOffer = `
  mutation SearchBookingExtensionOffer($input: SearchBookingExtensionOfferInput!) {
    searchBookingExtensionOffer(input: $input) {
        ...searchOfferFragment
    }
  }

  ${searchOfferFragment}
`;

const mutDeclareFinalExitForBooking = `
  mutation DeclareFinalExitForBooking($input: DeclareFinalExitForBookingInput!) {
    declareFinalExitForBooking(input: $input) {
      id
    }
}
`;

const mutRefundBooking = `
  mutation RefundBooking($input: RefundBookingInput!) {
    refundBooking(input: $input) {
      status
    }
  }
`;

const queGetBookingOffer = (hasPermission) => `
  query GetOffer($input: GetOfferInput!) {
    getOffer(input: $input) {
      ...offerFragment
    }
  }
  ${offerFragment(queryPermissions(hasPermission))}
`;

const mutBook = `
  mutation Book($input: BookInput!) {
    book(input: $input) {
      ...myBookingFragment
    }
  }

  ${myBookingFragment}
`;

// EXTEND BOOKING
const mutExtendBooking = `
  mutation ExtendBooking($input: ExtendBookingInput!) {
    extendBooking(input: $input) {
      ...myBookingFragment
    }
  }

  ${myBookingFragment}
`;

// PAY BOOKING OVERSTAY
const mutPayBookingOverstay = `
  mutation PayBookingOverstay($input: PayBookingOverstayInput!) {
    payBookingOverstay(input: $input) {
      ...purchasesForMyBookingFragment
    }
  }

  ${purchasesForMyBookingFragment}
`;

// CARPARK BOOKING
const mutAddBooking = `
  mutation AddBooking($input: AddBookingInput!) {
    addBooking(input: $input) {
      purchases {
        id,
        offers {
            ...addedBookingOfferFragment
          }
        }
      }
    }

  ${addedBookingOfferFragment}
`;

const mutUpdateBooking = `
  mutation UpdateBooking($input: UpdateBookingInput!) {
    updateBooking(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutUpdateBookingState = `
  mutation UpdateBookingState($input: UpdateBookingStateInput!) {
    updateBookingState(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutSendBookingPaymentLink = `
  mutation SendBookingPaymentLink($input: SendBookingPaymentLinkInput!) {
    sendBookingPaymentLink(input: $input)
  }
`;

const mutResendBookingConfirmationEmail = `
  mutation ResendBookingConfirmationEmail($input: ResendBookingConfirmationEmailInput!) {
    resendBookingConfirmationEmail(input: $input)
  }
`;

const mutCancelBooking = (hasPermission) => `
  mutation CancelBooking($input: CancelBookingInput!) {
    cancelBooking(input: $input) {
      ...bookingFragment
    }
  }

  ${bookingFragment(queryPermissions(hasPermission))}
`;

const mutCancelMyBooking = `
  mutation CancelMyBooking($input: CancelMyBookingInput!) {
    cancelMyBooking(input: $input) {
      ...myBookingFragment
    }
  }

  ${myBookingFragment}
`;

const queListOneparkBookings = `
  query ListOneparkBookings($input: ListOneparkBookingsInput!, $pagination: PaginationInput, $search: ListOneparkBookingsSearchInput, $sort: [ListOneparkBookingsSortInput]) {
    listOneparkBookings(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...oneparkBookingFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${oneparkBookingFragment}
`;

const queGetMyBooking = `
  query GetMyBooking($input: GetMyBookingInput!) {
    getMyBooking(input: $input) {
      ...myBookingFragment
    }
  }

  ${myBookingFragment}
`;

const queGetUpdatedMyBookingPurchases = `
  query GetUpdatedMyBooking($input: GetMyBookingInput!) {
    getMyBooking(input: $input) {
      ...myBookingWithPurchasesOnlyFragment
    }
  }

  ${myBookingWithPurchasesOnlyFragment}
`;

/**
 * Model
 */
const booking = {
  name: 'booking',
  icon: 'btnBookings',

  // Serialization
  serializeAdd: serializeBookingAdd,
  serializeUpdate: serializeBookingUpdate,
  serializeSearch: serializeBookingSearch,
  serializeExtendSearch: serializeExtendBookingSearch,
  serializePayBookingOverstay,
  serializeBooking,
  serializeExtendBooking,
  deserializeBookingOption,
  deserializeBooking,
  deserializeExtendBooking,
  deserializePayBookingOverstay,
  deserializeBookingOnepark,
  // List
  bookingOneparkStatusList,
  bookingOneparkUsageList,
  bookingStatusList,

  STATUS: mParking.STATUS_LIST,

  bookingStatus,
  isBookingCreated,
  isBookingInProgress,
  isBookingStarted,
  bookingOneparkStatus,
  isBookingCancelled,
  isBookingFinished,
  isBookingInOverstay,

  // List Bookings features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'status', dir: 'asc' },
        { field: 'begin', dir: 'desc' },
      ],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 2,
      md: 3,
      lg: 6,
      xl: 6,
    }),
    columns: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: true,
        secondarySorts: [{ field: 'begin', dir: 'desc' }],
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'carparkName',
        labelId: 'field.carpak',
        field: 'carparkName',
        sortable: true,
        className: 'hidden-md-down',
      },
      {
        key: 'reference',
        labelId: 'field.reference',
        field: 'reference',
        sortable: false,
      },
      {
        key: 'begin',
        labelId: 'field.beginDate',
        field: 'begin',
        className: 'hidden-sm-down',
        sortable: true,
      },
      {
        key: 'end',
        labelId: 'field.endDate',
        field: 'end',
        className: 'hidden-sm-down',
        sortable: true,
      },
      {
        key: 'payment',
        labelId: 'field.rate',
        field: 'payment',
        className: 'hidden-md-down',
        sortable: false,
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'status',
        },
      },
      {
        key: 'beginAfter',
        labelId: 'field.afterDateBegin',
        field: 'beginAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'beginBefore',
        labelId: 'field.beforeDateBegin',
        field: 'beginBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'endAfter',
        labelId: 'field.afterDateEnd',
        field: 'endAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'end',
        },
      },
      {
        key: 'endBefore',
        labelId: 'field.beforeDateEnd',
        field: 'endBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'end',
        },
      },
    ],
  },

  // Bookings list features
  bookingListFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'status', dir: 'asc' },
        { field: 'begin', dir: 'desc' },
      ],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'reference',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 1 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      sm: 1 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      md: 1 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      lg:
        4
        + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0)
        + (hasPermission({ needed: bookingWrite, permissionsOrContext: 'carparkPermissions' }) | 0),
      xl:
        5
        + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0)
        + (hasPermission({ needed: bookingWrite, permissionsOrContext: 'carparkPermissions' }) | 0),
    }),
    columns: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: true,
        secondarySorts: [{ field: 'begin', dir: 'desc' }],
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'reference',
        labelId: 'field.reference',
        field: 'reference',
        sortable: true,
        className: 'table-content-1 hidden-md-down',
      },
      {
        key: 'begin',
        labelId: 'field.beginDate',
        field: 'begin',
        sortable: true,
        className: 'hidden-md-down',
      },
      {
        key: 'end',
        labelId: 'field.endDate',
        field: 'end',
        sortable: true,
        className: 'hidden-md-down',
      },
      {
        key: 'customer',
        permissions: {
          needed: customerRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.carparkUser',
        field: 'customerFullName',
        sortable: true,
        secondarySorts: [{ field: 'begin', dir: 'desc' }],
      },
      {
        key: 'payment',
        labelId: 'field.paid',
        field: 'payment',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'occupancy',
        labelId: 'field.occupancy',
        field: 'occupancy',
        sortable: false,
        className: 'table-content-2 hidden-lg-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'parkingCategoryId',
        permissions: {
          needed: parkingCategoryRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.parkingCategory',
        field: 'parkingCategoryId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'parkingCategoryId',
        },
      },
      {
        key: 'reference',
        labelId: 'field.reference',
        field: 'reference',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'reference',
        },
      },
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'status',
        },
      },
      {
        key: 'carparkCustomer',
        permissions: {
          needed: customerRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.carparkUser',
        field: 'carparkCustomer',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'customerId',
          key: 'id',
        },
      },
      {
        key: 'keypadCode',
        labelId: 'field.keypadCode',
        field: 'keypadCode',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'keypadCode',
        },
      },
      {
        key: 'licensePlate',
        labelId: 'field.licensePlate',
        field: 'licensePlate',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'licensePlate',
        },
      },
      {
        key: 'beginAfter',
        labelId: 'field.afterDateBegin',
        field: 'beginAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'beginBefore',
        labelId: 'field.beforeDateBegin',
        field: 'beginBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'endAfter',
        labelId: 'field.afterDateEnd',
        field: 'endAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'end',
        },
      },
      {
        key: 'endBefore',
        labelId: 'field.beforeDateEnd',
        field: 'endBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'end',
        },
      },
    ],
  },

  // Onepark bookings list features
  bookingOneparkListFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'status', dir: 'asc' },
        { field: 'begin', dir: 'desc' },
      ],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'reference',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: () => ({
      xs: 3,
      sm: 3,
      md: 3,
      lg: 8,
      xl: 8,
    }),
    columns: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: true,
        secondarySorts: [{ field: 'begin', dir: 'desc' }],
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'bookingId',
        labelId: 'field.bookingId',
        field: 'bookingId',
        sortable: false,
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'begin',
        labelId: 'field.beginDate',
        field: 'begin',
        sortable: true,
        className: 'hidden-md-down',
      },
      {
        key: 'end',
        labelId: 'field.endDate',
        field: 'end',
        sortable: true,
        className: 'hidden-md-down',
      },
      {
        key: 'bookingUsage',
        labelId: 'field.type',
        field: 'bookingUsage',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'customerLastname',
        labelId: 'field.client',
        field: 'customerLastname',
        sortable: true,
      },
      {
        key: 'licensePlate',
        labelId: 'field.licensePlate',
        field: 'licensePlate',
        className: 'hidden-md-down',
        sortable: false,
      },
      {
        key: 'keypadCode',
        labelId: 'field.keypadCode',
        field: 'keypadCode',
        sortable: false,
        className: 'hidden-md-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'status',
        },
      },
      {
        key: 'bookingId',
        labelId: 'field.bookingId',
        field: 'bookingId',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'bookingId',
        },
      },
      {
        key: 'beginAfter',
        labelId: 'field.afterDateBegin',
        field: 'beginAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'beginBefore',
        labelId: 'field.beforeDateBegin',
        field: 'beginBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'endAfter',
        labelId: 'field.afterDateEnd',
        field: 'endAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'end',
        },
      },
      {
        key: 'endBefore',
        labelId: 'field.beforeDateEnd',
        field: 'endBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'end',
        },
      },
      {
        key: 'bookingUsage',
        labelId: 'field.type',
        field: 'bookingUsage',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'bookingUsage',
        },
      },
      {
        key: 'customerLastname',
        labelId: 'field.client',
        field: 'customerLastname',
        initial: '',
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'customerLastname',
        },
      },
      {
        key: 'licensePlate',
        labelId: 'field.licensePlate',
        field: 'licensePlate',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'licensePlate',
        },
      },
      {
        key: 'keypadCode',
        labelId: 'field.keypadCode',
        field: 'keypadCode',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'keypadCode',
        },
      },
    ],
  },

  // GraphQL
  queListBookings,
  queListMyBookings,
  queListMyBookingOptions,
  queSearchBookingOffer,
  mutSearchBookingExtensionOffer,
  mutDeclareFinalExitForBooking,
  mutRefundBooking,
  queListOneparkBookings,
  queGetMyBooking,
  queGetBookingOffer,
  queGetUpdatedMyBookingPurchases,
  mutBook,
  mutExtendBooking,
  mutPayBookingOverstay,
  mutAddBooking,
  mutUpdateBooking,
  mutUpdateBookingState,
  mutSendBookingPaymentLink,
  mutResendBookingConfirmationEmail,
  mutCancelBooking,
  mutCancelMyBooking,
};

module.exports = booking;



// WEBPACK FOOTER //
// ./models/cpm/booking.js